import fileDownload from 'js-file-download';
import api, { IPaginationResponse } from 'config/axios';
import AppService from 'common/app-service';
import {
  ICreateRevenue,
  IRevenue,
  RevenueStatus,
  ICreateRevenueConciliation,
  ISettlementRevenueConciliationResponse,
  ISettlementCompositionPreview,
  IRevenueCorrection,
  RevenueOrigin,
} from 'common/revenue.interface';
import { ICteConciliation } from 'common/cte.interface';

export type TRevenueFilters = {
  paymentDate?: string | undefined;
  paymentDateStart?: string | undefined;
  paymentDateEnd?: string | undefined;
  settlementDate?: string | undefined;
  carrierCompanyName?: string | undefined;
  carrierTradeName?: string | undefined;
  takerCorporateName?: string | undefined;
  takerTradeName?: string | undefined;
  origin?: RevenueOrigin;
  status?: RevenueStatus;
};

export type RevenueCorrectionFilters = {
  correctionDate?: string | undefined;
  carrierCompanyName?: string | undefined;
  carrierTradeName?: string | undefined;
  takerCorporateName?: string | undefined;
  takerTradeName?: string | undefined;
};

export default class RevenueService extends AppService {
  constructor() {
    super('revenues', api);
  }

  async getAll(page = 1, filters?: TRevenueFilters): Promise<IPaginationResponse<IRevenue>> {
    const response = await this.api.get(this.root(), { params: { page, ...filters } });
    return response.data;
  }

  async create(data: ICreateRevenue): Promise<IRevenue> {
    const response = await this.api.post(this.root(), data);

    return response.data;
  }

  async delete(id: number) {
    await this.api.delete(this.root(id));
  }

  async changeOrigin(id: number, origin: RevenueOrigin) {
    const response = await this.api.patch(this.root(`${id}/origin`), { origin });
    return response.data;
  }

  async getLiquidationData(revenueId: number): Promise<IPaginationResponse<ICteConciliation>> {
    const response = await this.api.get(this.root(`${revenueId}/contracted-liquidation-data`));
    return response.data;
  }

  /**
   * TAKER only: overdue, unsettled titles for the revenue's carrier, across
   * any financiador and any tomador -- the "Tabela de Vencidos" flow,
   * separate from the uploaded composição. `excludedDfeIds` should be the
   * titles already selected in the composição table, so they don't reappear
   * here.
   */
  async getCarrierOverdueTitles(revenueId: number, excludedDfeIds: number[] = []): Promise<IPaginationResponse<ICteConciliation>> {
    const response = await this.api.get(this.root(`${revenueId}/carrier-overdue-titles`), {
      params: excludedDfeIds.length > 0 ? { excludedDfeIds: excludedDfeIds.join(',') } : undefined,
    });
    return response.data;
  }

  async getCtesFromCsv(revenueId: number, file: File): Promise<IPaginationResponse<ICteConciliation>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(this.root(`${revenueId}/liquidation-data`), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: () => {
        // known issue to perform a formData post https://github.com/axios/axios/issues/4406
        return formData;
      },
    });
    return response.data;
  }

  async getSettlementComposition(
    revenueId: number,
    params: {
      dfeIds?: number[];
      chargesByRowKey?: Record<string, number>;
      correctionAmount?: number;
      accountPermanenceAmount?: number;
      /** TAKER-origin only: titles manually picked from the carrier-wide "Tabela de Vencidos". */
      manualOverdueDfeIds?: number[];
      /** FINANCIER-origin only: user-entered amount destined to IPEF (sent via Pamcard). */
      ipefAmount?: number;
    }
  ): Promise<ISettlementCompositionPreview> {
    const response = await this.api.post(this.root(`${revenueId}/settlement-composition`), params);
    return response.data;
  }

  async downloadSettlementCompositionTitlesXlsx(revenueId: number, dfeIds: number[]) {
    const response = await this.api.get(this.root(`${revenueId}/settlement-composition/titles/xlsx`), {
      params: { dfe_ids: dfeIds.join(',') },
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      data: {},
      responseType: 'blob',
    });
    fileDownload(response.data, `titulos_composicao_${revenueId}.xlsx`);
  }

  async conciliateRevenue(revenueId: number, data: ICreateRevenueConciliation): Promise<ISettlementRevenueConciliationResponse> {
    const response = await this.api.post(this.root(revenueId), data);
    return response.data;
  }

  async removeConciliation(revenueId: number) {
    const response = await this.api.delete(this.root(revenueId));

    return response.data;
  }

  async getRevenue(id: number): Promise<IRevenue> {
    const response = await this.api.get(this.root(id));

    return response.data;
  }

  async getCorrections(page = 1, filters?: RevenueCorrectionFilters): Promise<IPaginationResponse<IRevenueCorrection>> {
    const response = await this.api.get(this.root('corrections'), { params: { page, ...filters } });

    return response.data;
  }

  async getCorrectionsCsv(page = 1, filters?: RevenueCorrectionFilters) {
    const response = await this.api.get(this.root('corrections'), {
      params: { page, ...filters },
      headers: { 'Content-Type': 'text/csv' },
      data: {},
      responseType: 'blob',
    });
    fileDownload(response.data, 'arrecadacao-ajustes.csv');
  }

  async downloadXlsx(revenueId: number) {
    const response = await this.api.get(this.root(revenueId), {
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      data: {},
      responseType: 'blob',
    });
    fileDownload(response.data, `conciliacao_ctes_${revenueId}.xlsx`);
  }
}
